import produce from "immer";
import {
  AI_IMPORT_STARTED,
  AI_IMPORT_QUEUED,
  AI_IMPORT_STEP_STARTED,
  AI_IMPORT_STEP_COMPLETED,
  AI_IMPORT_STEP_FAILED,
  AI_IMPORT_ALL_COMPLETED,
  AI_IMPORT_ALL_FAILED,
  AI_IMPORT_DISMISS,
  AI_IMPORT_MODAL_TOGGLE,
  AI_IMPORT_UPLOAD_FAILED,
} from "../types";

const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  fileName: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  projects: [],
  // Carried over from ALL_COMPLETED so the caller (AIProjectImport view) can
  // navigate to the newly created project once the user dismisses the dialog.
  resultProjectId: null,
  resultScenarioId: null,
  resultStructureId: null,
};

const initialState = { jobs: {} };

function upsertProject(job, itemIndex, patch) {
  if (itemIndex == null) return;
  const idx = job.projects.findIndex((p) => p.index === itemIndex);
  if (idx === -1) {
    job.projects.push({
      index: itemIndex,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      weight: 1,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

export default function aiImportReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case AI_IMPORT_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest.
        const prev = draft.jobs[jobId];
        draft.jobs[jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          ...payload,
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload?.startedAt ?? Date.now(),
        };
      });

    case AI_IMPORT_QUEUED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = true;
        job.totalProjects = payload.total ?? (payload.projects?.length ?? 0);
        if (payload.avgSecondsPerWeight != null) {
          job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        }
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        job.projects = (payload.projects || []).map((p) => ({
          index: p.index,
          label: p.label,
          weight: p.weight ?? 1,
          status: PROJECT_STATUS.QUEUED,
          startedAt: null,
          completedAt: null,
          error: null,
        }));
      });

    case AI_IMPORT_STEP_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? Date.now(),
        });
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AI_IMPORT_STEP_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.COMPLETED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AI_IMPORT_STEP_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.FAILED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: payload.error || "Unknown error",
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case AI_IMPORT_ALL_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = Date.now();
        if (payload?.result) {
          job.resultProjectId = payload.result.projectId ?? null;
          job.resultScenarioId = payload.result.scenarioId ?? null;
          job.resultStructureId = payload.result.structureId ?? null;
        }
      });

    case AI_IMPORT_ALL_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Import failed";
        job.terminatedAt = Date.now();
      });

    case AI_IMPORT_UPLOAD_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Upload failed";
        job.terminatedAt = Date.now();
      });

    case AI_IMPORT_MODAL_TOGGLE:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.open = payload?.open ?? !job.open;
      });

    case AI_IMPORT_DISMISS:
      return produce(state, (draft) => {
        const jobId = payload?.jobId ?? payload;
        if (jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) {
              delete draft.jobs[id];
            }
          });
        } else {
          delete draft.jobs[jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
